"use client"
import { ChevronDown, Plus, CalendarIcon, Clock, Search, X, Camera, Eye, EyeOff } from "lucide-react"
import type React from "react"
import { format, parse, isValid as isValidDate } from "date-fns"

import { useState, useRef, useCallback } from "react"
import { Popover, PopoverContent, PopoverTrigger } from "@edemics/ui/popover"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@edemics/ui/dialog"
import { Button } from "@edemics/ui/button"
import { DatePickerDialog } from "@edemics/ui/date-picker-dialog"
import { TimePickerDialog } from "@edemics/ui/time-picker-dialog"
import { DateTimePickerDialog } from "@edemics/ui/date-time-picker-dialog"
import { cn } from "@edemics/ui/lib/utils"

/** Renders a single link-type dropdown option with a bold primary label and a
 *  muted secondary line (subtitle if provided, otherwise the raw value). */
const LinkOptionItem = ({
  option,
  selected,
}: {
  option: { value: string; label: string; subtitle?: string }
  selected: boolean
}) => (
  <div
    className={cn(
      "w-full px-3 py-2.5 text-left rounded-lg",
      selected && "ring-2 ring-inset ring-[#8C57FF]"
    )}
  >
    {/* Primary — bold name / code */}
    <div className="text-sm font-bold text-gray-900 dark:text-gray-100 leading-snug">
      {option.label}
    </div>
    {/* Secondary — subtitle or raw value when they differ */}
    {(option.subtitle || option.value !== option.label) && (
      <div className="text-xs text-[#8C57FF] dark:text-[#A87FF5] mt-0.5 leading-snug font-normal">
        {(option.subtitle ?? option.value).split("\n").map((line, index) => (
          <div key={index}>{line}</div>
        ))}
      </div>
    )}
  </div>
)

interface StandardInputFieldProps {
  label: string
  value: string
  onChange?: (value: string) => void
  type?: "text" | "email" | "tel" | "textarea" | "select" | "color-select" | "link" | "date" | "time" | "datetime" | "radio" | "multiselect" | "tag" | "barcode" | "password"
  /** For type="password": called on the native paste event. Use `e.preventDefault()` to block paste. */
  onPaste?: (e: React.ClipboardEvent<HTMLInputElement>) => void
  /** For multiselect: array of currently selected values */
  selectedValues?: string[]
  /** For multiselect: callback when selection changes */
  onMultiChange?: (values: string[]) => void
  /** For tag: array of current tags */
  tags?: string[]
  /** For tag: callback when tags change */
  onTagsChange?: (tags: string[]) => void
  options?: { value: string; label: string; subtitle?: string; data?: any }[]
  radioDirection?: "horizontal" | "vertical"
  placeholder?: string
  required?: boolean
  className?: string
  rows?: number
  onAddNew?: () => void
  readOnly?: boolean
  valueClassName?: string
  onBlur?: () => void
  onClick?: () => void
  icon?: React.ReactNode
  error?: boolean
  helperText?: string
  dropdownWidth?: string
  searchable?: boolean
  searchValue?: string
  onSearchChange?: (value: string) => void
  /** When true, required fields with empty values auto-show error styling. Set to true on all fields when Save is clicked. */
  touched?: boolean
  /** Ref for textarea element to maintain cursor position */
  textareaRef?: React.RefObject<HTMLTextAreaElement>
  /** Custom label for the "Add New" button. If not provided, uses "Add New {label}" */
  addNewLabel?: string
  /** Override the label background class (e.g. when placed on a non-white/card background) */
  labelBg?: string
  /** Override the label background via inline style (takes precedence over labelBg) */
  labelStyle?: React.CSSProperties
  /** When true, the field is read-only and visually dimmed */
  disabled?: boolean
  /**
   * For multiselect: when true, hides the trailing chevron icon. Used when
   * the multiselect should look like a "linked field" (no caret).
   */
  hideChevron?: boolean
  /**
   * When true, the field is rendered inside a table cell:
   *  - the rounded outline border around the trigger/input is suppressed
   *    (so the cell's column divider is the only visible border), and
   *  - the floating top-left label is hidden (the column header already
   *    labels the field).
   * `EditableDataTable` injects this automatically via `cloneElement`
   * for any field returned from a column's `cellRenderer`.
   */
  cellMode?: boolean
  /**
   * For date / datetime fields: the primary display + parse format string
   * (date-fns syntax). Defaults to "dd/MM/yyyy" for date, "dd/MM/yyyy HH:mm:ss"
   * for datetime. Common fallback formats are always tried automatically.
   */
  format?: string
  /** Text appended as a read-only suffix badge inside the text input (e.g. ".edemics.com") */
  suffix?: string
  /**
   * When true, the field renders as a bordered box with italic muted placeholder text
   * ("Auto-populated"). Checked before readOnly — takes precedence over it.
   */
  autoPopulated?: boolean
}

export function StandardInputField({
  label,
  value,
  onChange,
  type = "text",
  options = [],
  radioDirection,
  placeholder = "",
  required = false,
  className = "",
  rows = 4,
  onAddNew,
  addNewLabel,
  readOnly = false,
  valueClassName = "",
  textareaRef,
  onBlur,
  onClick,
  icon,
  error = false,
  helperText = "",
  dropdownWidth,
  searchable = false,
  searchValue = "",
  onSearchChange,
  touched = false,
  labelBg,
  labelStyle,
  selectedValues = [],
  onMultiChange,
  tags = [],
  onTagsChange,
  disabled = false,
  hideChevron = false,
  cellMode = false,
  format: formatProp,
  suffix,
  autoPopulated = false,
  onPaste,
}: StandardInputFieldProps) {
  const [isLinkOpen, setIsLinkOpen] = useState(false)
  const [isDateOpen, setIsDateOpen] = useState(false)
  const [isTimeOpen, setIsTimeOpen] = useState(false)
  const [isDateTimeOpen, setIsDateTimeOpen] = useState(false)
  const [isMultiOpen, setIsMultiOpen] = useState(false)
  const [linkSearch, setLinkSearch] = useState("")
  const [multiSearch, setMultiSearch] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const selectingRef = useRef(false)
  // Improved-link state: when the user has a selection and starts
  // typing in the trigger input, we capture their typed text in
  // `editingText` instead of immediately clearing the selection. The
  // popover filters by `editingText` and only commits a new value when
  // an option is clicked. `openingRef` deduplicates the
  // mousedown/focus pair that fires when clicking the input on an
  // empty field, preventing the popover from opening twice.
  const [editingText, setEditingText] = useState<string | null>(null)
  const openingRef = useRef(false)
  const linkInputRef = useRef<HTMLInputElement>(null)
  const [tagInput, setTagInput] = useState("")
  const [tagFocused, setTagFocused] = useState(false)
  const tagInputRef = useRef<HTMLInputElement>(null)

  // ── Date / Time / DateTime inline-typing state ─────────────────────────
  // Each field type has its own "editingText" so the user can type freely
  // while the popover stays open.  A null means "not editing" (show formatted value).
  const [dateEditText, setDateEditText] = useState<string | null>(null)
  const [timeEditText, setTimeEditText] = useState<string | null>(null)
  const [datetimeEditText, setDatetimeEditText] = useState<string | null>(null)
  const dateSelectingRef = useRef(false)
  const dateOpeningRef = useRef(false)
  const timeSelectingRef = useRef(false)
  const timeOpeningRef = useRef(false)
  const dtSelectingRef = useRef(false)
  const dtOpeningRef = useRef(false)
  const dateInputRef = useRef<HTMLInputElement>(null)
  const timeInputRef = useRef<HTMLInputElement>(null)
  const dtInputRef = useRef<HTMLInputElement>(null)
  // Refs to the popover content containers so blur can check if focus moved inside.
  const datePopoverRef = useRef<HTMLDivElement>(null)
  const timePopoverRef = useRef<HTMLDivElement>(null)
  const dtPopoverRef = useRef<HTMLDivElement>(null)

  // ── Parse helpers — mirror DatePickerField exactly ─────────────────────
  // The caller-supplied `formatProp` is always tried first; these fallbacks
  // cover every common variant the user might type.
  const DATE_FALLBACK_FORMATS = [
    "dd/MM/yyyy", "d/M/yyyy", "MM/dd/yyyy", "M/d/yyyy",
    "yyyy-MM-dd", "d MMM yyyy", "dd MMM yyyy",
    "MMM d yyyy", "MMM d, yyyy", "MMMM d yyyy", "MMMM d, yyyy",
  ]
  const DATE_DISPLAY_FORMAT = formatProp ?? "dd/MM/yyyy"
  const DATETIME_DISPLAY_FORMAT = formatProp ?? "dd/MM/yyyy HH:mm:ss"
  const DATETIME_FALLBACK_FORMATS = [
    "dd/MM/yyyy HH:mm:ss", "dd/MM/yyyy HH:mm",
    "yyyy-MM-dd HH:mm:ss", "yyyy-MM-dd HH:mm", "yyyy-MM-dd'T'HH:mm:ss",
  ]

  const tryParseDateInput = useCallback((text: string, primary = DATE_DISPLAY_FORMAT): Date | null => {
    const trimmed = text.trim()
    if (!trimmed) return null
    // Try primary first, then fallbacks (deduped).
    const candidates = [primary, ...DATE_FALLBACK_FORMATS.filter(f => f !== primary)]
    for (const f of candidates) {
      const d = parse(trimmed, f, new Date())
      if (isValidDate(d)) return d
    }
    const fb = new Date(trimmed)
    return isValidDate(fb) ? fb : null
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [DATE_DISPLAY_FORMAT])

  const tryParseDateTimeInput = useCallback((text: string, primary = DATETIME_DISPLAY_FORMAT): Date | null => {
    const trimmed = text.trim()
    if (!trimmed) return null
    const candidates = [primary, ...DATETIME_FALLBACK_FORMATS.filter(f => f !== primary)]
    for (const f of candidates) {
      const d = parse(trimmed, f, new Date())
      if (isValidDate(d)) return d
    }
    const fb = new Date(trimmed)
    return isValidDate(fb) ? fb : null
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [DATETIME_DISPLAY_FORMAT])

  const tryParseTimeInput = useCallback((text: string): string | null => {
    const trimmed = text.trim()
    if (!trimmed) return null
    // Accept HH:MM:SS, HH:MM, H:MM — also handle 12-hour "HH:MM PM" input.
    const m12 = trimmed.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)$/i)
    if (m12) {
      let h = parseInt(m12[1])
      const min = Math.min(59, parseInt(m12[2]))
      const sec = Math.min(59, parseInt(m12[3] ?? "0"))
      const pm = m12[4].toUpperCase() === "PM"
      if (pm && h < 12) h += 12
      if (!pm && h === 12) h = 0
      return `${String(h).padStart(2,"0")}:${String(min).padStart(2,"0")}:${String(sec).padStart(2,"0")}`
    }
    const m = trimmed.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/)
    if (m) {
      const h = Math.min(23, parseInt(m[1]))
      const min = Math.min(59, parseInt(m[2]))
      const sec = Math.min(59, parseInt(m[3] ?? "0"))
      return `${String(h).padStart(2,"0")}:${String(min).padStart(2,"0")}:${String(sec).padStart(2,"0")}`
    }
    return null
  }, [])

  const addTag = (val: string) => {
    const trimmed = val.trim().replace(/,$/, "")
    if (!trimmed || tags.includes(trimmed)) return
    onTagsChange?.([...tags, trimmed])
  }
  const handleTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault()
      addTag(tagInput)
      setTagInput("")
    } else if (e.key === "Backspace" && !tagInput && tags.length > 0) {
      onTagsChange?.(tags.slice(0, -1))
    }
  }
  const handleTagChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    if (val.endsWith(",")) {
      addTag(val)
      setTagInput("")
    } else {
      setTagInput(val)
    }
  }

  // Auto-validation: if required, touched, and empty -> show error
  const autoError = touched && required && !(value ?? "").trim()
  const isError = error || autoError
  const displayHelperText = autoError && !helperText ? `${label} is required` : helperText

  const parseDate = (dateStr: string): Date | undefined => {
    if (!dateStr) return undefined
    // Try parsing YYYY-MM-DD format first
    try {
      const parsed = parse(dateStr, "yyyy-MM-dd", new Date())
      if (!isNaN(parsed.getTime())) return parsed
    } catch {}
    // Try parsing other common formats
    try {
      const parsed = new Date(dateStr)
      if (!isNaN(parsed.getTime())) return parsed
    } catch {}
    return undefined
  }

  const formatDisplayDate = (dateStr: string): string => {
    const date = parseDate(dateStr)
    if (!date) return placeholder || "Select date"
    return format(date, "PPP") // e.g., "January 1, 2025"
  }

  // Shared read-only display.
  // The outer wrapper's floating label renders on top of it as normal.
  const renderReadOnly = (displayValue: string) => (
    <div
      className={cn(
        "w-full px-3 py-3 border-[2.5px] rounded-md font-normal text-base leading-[1.5em] cursor-not-allowed h-[53px] flex items-center",
  // inside a table cell the value is just dimmed: no grey fill, no outline
  cellMode ? "bg-transparent border-transparent" : "bg-gray-100 dark:bg-[#28243D]",
  isError
  ? "border-red-300 text-red-400"
  : cellMode
  ? "text-gray-500 dark:text-gray-400"
  : "border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400",
  valueClassName
      )}
    >
      <div className="flex items-center justify-between w-full opacity-60">
        <span>{displayValue || placeholder}</span>
        {icon}
      </div>
    </div>
  )

  const renderInput = () => {
    // autoPopulated takes precedence over readOnly.
    if (autoPopulated) {
      return (
        <div
          className={cn(
            "w-full px-3 py-3 border-2 rounded-md bg-transparent",
            isError
              ? "border-red-500 text-red-500"
              : "border-secondary/[0.3]",
            valueClassName
          )}
          onClick={onClick}
        >
          <span className="text-sm italic text-muted-foreground">
            {value || "Auto-populated"}
          </span>
        </div>
      )
    }

    if (type === "password") {
      if (readOnly) return renderReadOnly("********")
      return (
        <div
          className={cn(
            "relative w-full flex items-center border-[2.5px] rounded-md transition-colors",
            isError
              ? "border-red-500"
              : "border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 focus-within:border-[#8C57FF]",
            disabled && "opacity-50 cursor-not-allowed",
          )}
        >
          <input
            type={showPassword ? "text" : "password"}
            value={value ?? ""}
            placeholder={placeholder}
            disabled={disabled}
            autoComplete="new-password"
            onPaste={onPaste}
            onChange={(e) => onChange?.(e.target.value)}
            onBlur={onBlur}
            className={cn(
              "flex-1 bg-transparent text-base leading-[1.5em] py-3 pl-3 pr-10 focus:outline-none focus:ring-0",
              isError
                ? "text-red-500 placeholder:text-red-300"
                : "text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500",
              disabled && "cursor-not-allowed",
              valueClassName,
            )}
          />
          <button
            type="button"
            tabIndex={-1}
            aria-label={showPassword ? "Hide password" : "Show password"}
            onClick={() => setShowPassword((p) => !p)}
            className="absolute right-3 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
      )
    }

    if (type === "date") {
      if (readOnly) {
        const committedDate = value ? tryParseDateInput(value) ?? parseDate(value) ?? undefined : undefined
        const display = committedDate ? format(committedDate, DATE_DISPLAY_FORMAT) : ""
        return renderReadOnly(display)
      }
      // Derive the committed Date from the stored ISO/free-form value string.
      const committedDate = value ? tryParseDateInput(value) ?? parseDate(value) ?? undefined : undefined
      // What the input shows: typing text when editing, else the formatted committed date.
      const dateDisplay = dateEditText !== null
        ? dateEditText
        : committedDate ? format(committedDate, DATE_DISPLAY_FORMAT) : ""

      const commitDate = () => {
        if (dateEditText === null) return
        if (dateEditText === "") {
          onChange?.("")
        } else {
          const parsed = tryParseDateInput(dateEditText, DATE_DISPLAY_FORMAT)
          if (parsed) onChange?.(format(parsed, "yyyy-MM-dd"))
          // Junk text: silently revert (don't call onChange).
        }
        setDateEditText(null)
      }

      return (
        <Popover open={isDateOpen} onOpenChange={() => { /* controlled manually */ }}>
          <PopoverTrigger asChild>
            {/* Wrapper div acts as the click area; the input sits inside it. */}
            <div
              className={cn(
                "relative w-full flex items-center border-[2.5px] rounded-md transition-colors cursor-text",
                isError
                  ? "border-red-500"
                  : isDateOpen
                    ? "border-[#8C57FF]"
                    : "border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500",
                disabled && "opacity-50 cursor-not-allowed",
              )}
              onMouseDown={() => {
                if (disabled) return
                if (!isDateOpen) {
                  dateOpeningRef.current = true
                  setIsDateOpen(true)
                  window.setTimeout(() => { dateOpeningRef.current = false }, 300)
                }
                window.setTimeout(() => dateInputRef.current?.focus(), 0)
              }}
            >
              <input
                ref={dateInputRef}
                type="text"
                value={dateDisplay}
                placeholder={placeholder || DATE_DISPLAY_FORMAT.toUpperCase()}
                disabled={disabled}
                inputMode="numeric"
                autoComplete="off"
                onMouseDown={() => {
                  // Input itself clicked — same guard as wrapper so onFocus
                  // doesn't double-open when the user clicks the text area.
                  if (disabled) return
                  if (!isDateOpen) {
                    dateOpeningRef.current = true
                    setIsDateOpen(true)
                    window.setTimeout(() => { dateOpeningRef.current = false }, 300)
                  }
                }}
                onChange={(e) => {
                  const next = e.target.value
                  setDateEditText(next)
                  if (!isDateOpen) setIsDateOpen(true)
                  const parsed = tryParseDateInput(next, DATE_DISPLAY_FORMAT)
                  if (parsed) onChange?.(format(parsed, "yyyy-MM-dd"))
                  // Cleared → remove calendar highlight.
                  else if (!next.trim()) onChange?.("")
                }}
                onFocus={() => {
                  if (disabled) return
                  if (!isDateOpen && !dateOpeningRef.current) setIsDateOpen(true)
                }}
                onBlur={() => {
                  window.setTimeout(() => {
                    if (dateSelectingRef.current || dateOpeningRef.current) return
                    // If focus moved into the calendar popover, keep it open.
                    if (datePopoverRef.current?.contains(document.activeElement)) return
                    commitDate()
                    setIsDateOpen(false)
                    onBlur?.()
                  }, 200)
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") { e.preventDefault(); commitDate(); setIsDateOpen(false); dateInputRef.current?.blur() }
                  else if (e.key === "Escape") { setDateEditText(null); setIsDateOpen(false); dateInputRef.current?.blur() }
                }}
                className={cn(
                  "flex-1 min-w-0 bg-transparent text-base leading-[1.5em] py-3 pl-3 pr-1 focus:outline-none focus:ring-0",
                  isError ? "text-red-500 placeholder:text-red-300" : "text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500",
                  disabled && "cursor-not-allowed",
                )}
              />
              <span
                className="pr-3 cursor-pointer shrink-0"
                onMouseDown={(e) => {
                  e.preventDefault() // prevent input blur
                  e.stopPropagation()
                  setIsDateOpen((prev) => !prev)
                  window.setTimeout(() => dateInputRef.current?.focus(), 0)
                }}
              >
                <CalendarIcon className={cn("h-4 w-4", isError ? "text-red-500" : "text-gray-400 dark:text-gray-500")} />
              </span>
            </div>
          </PopoverTrigger>
          <PopoverContent
            onWheel={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
            className="w-auto p-0 bg-transparent border-none shadow-none"
            align="start"
            onOpenAutoFocus={(e) => e.preventDefault()}
            onInteractOutside={(e) => {
              if (dateSelectingRef.current || dateOpeningRef.current) { e.preventDefault(); return }
              commitDate()
              setIsDateOpen(false)
            }}
          >
            <div
              ref={datePopoverRef}
              onPointerDown={(e) => { if ((e.target as HTMLElement).closest("button")) return; dateSelectingRef.current = true }}
              onPointerUp={() => { window.setTimeout(() => { dateSelectingRef.current = false }, 0) }}
            >
              <DatePickerDialog
                value={committedDate}
                onChange={(date) => {
                  onChange?.(format(date, "yyyy-MM-dd"))
                  setDateEditText(null)
                  setIsDateOpen(false)
                  window.setTimeout(() => dateInputRef.current?.focus(), 0)
                }}
                onClose={() => setIsDateOpen(false)}
              />
            </div>
          </PopoverContent>
        </Popover>
      )
    }

    if (type === "time") {
      if (readOnly) {
        const formatTimeDisplay = (v: string) => {
          if (!v) return ""
          const parts = v.split(":")
          const h = parseInt(parts[0]) || 0
          const m = parseInt(parts[1]) || 0
          const s = parseInt(parts[2]) || 0
          const period = h >= 12 ? "PM" : "AM"
          const h12 = h % 12 === 0 ? 12 : h % 12
          return `${String(h12).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")} ${period}`
        }
        return renderReadOnly(formatTimeDisplay(value))
      }
      // Display: editing text when typing, else 12-hour formatted value.
      const formatTimeDisplay = (v: string) => {
        if (!v) return ""
        const parts = v.split(":")
        const h = parseInt(parts[0]) || 0
        const m = parseInt(parts[1]) || 0
        const s = parseInt(parts[2]) || 0
        const period = h >= 12 ? "PM" : "AM"
        const h12 = h % 12 === 0 ? 12 : h % 12
        return `${String(h12).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")} ${period}`
      }
      const timeDisplay = timeEditText !== null ? timeEditText : formatTimeDisplay(value)

      const commitTime = () => {
        if (timeEditText === null) return
        if (!timeEditText.trim()) { onChange?.("") }
        else {
          const parsed = tryParseTimeInput(timeEditText)
          if (parsed) onChange?.(parsed)
        }
        setTimeEditText(null)
      }

      return (
        <Popover open={isTimeOpen} onOpenChange={() => {}}>
          <PopoverTrigger asChild>
            <div
              className={cn(
                "relative w-full flex items-center border-[2.5px] rounded-md transition-colors cursor-text",
                isError
                  ? "border-red-500"
                  : isTimeOpen
                    ? "border-[#8C57FF]"
                    : "border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500",
                disabled && "opacity-50 cursor-not-allowed",
              )}
              onMouseDown={() => {
                if (disabled) return
                if (!isTimeOpen) {
                  timeOpeningRef.current = true
                  setIsTimeOpen(true)
                  window.setTimeout(() => { timeOpeningRef.current = false }, 300)
                }
                window.setTimeout(() => timeInputRef.current?.focus(), 0)
              }}
            >
              <input
                ref={timeInputRef}
                type="text"
                value={timeDisplay}
                placeholder={placeholder || "HH:MM:SS AM/PM"}
                disabled={disabled}
                autoComplete="off"
                onMouseDown={() => {
                  if (disabled) return
                  if (!isTimeOpen) {
                    timeOpeningRef.current = true
                    setIsTimeOpen(true)
                    window.setTimeout(() => { timeOpeningRef.current = false }, 300)
                  }
                }}
                onChange={(e) => {
                  setTimeEditText(e.target.value)
                  if (!isTimeOpen) setIsTimeOpen(true)
                  const parsed = tryParseTimeInput(e.target.value)
                  if (parsed) onChange?.(parsed)
                  else if (!e.target.value.trim()) onChange?.("")
                }}
                onFocus={() => { if (!isTimeOpen && !timeOpeningRef.current) setIsTimeOpen(true) }}
                onBlur={() => {
                  window.setTimeout(() => {
                    if (timeSelectingRef.current || timeOpeningRef.current) return
                    // If focus moved into the time popover (e.g. HH:MM:SS inputs), keep open.
                    if (timePopoverRef.current?.contains(document.activeElement)) return
                    commitTime(); setIsTimeOpen(false); onBlur?.()
                  }, 200)
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") { e.preventDefault(); commitTime(); setIsTimeOpen(false); timeInputRef.current?.blur() }
                  else if (e.key === "Escape") { setTimeEditText(null); setIsTimeOpen(false); timeInputRef.current?.blur() }
                }}
                className={cn(
                  "flex-1 bg-transparent text-base leading-[1.5em] py-3 pl-3 pr-1 focus:outline-none focus:ring-0",
                  isError ? "text-red-500 placeholder:text-red-300" : "text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500",
                  disabled && "cursor-not-allowed",
                )}
              />
              <span
                className="pr-3 cursor-pointer"
                onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setIsTimeOpen((p) => !p); window.setTimeout(() => timeInputRef.current?.focus(), 0) }}
              >
                <Clock className={cn("h-4 w-4", isError ? "text-red-500" : "text-gray-400 dark:text-gray-500")} />
              </span>
            </div>
          </PopoverTrigger>
          <PopoverContent
            onWheel={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
            className="w-auto p-0 bg-transparent border-none shadow-none"
            align="start"
            onOpenAutoFocus={(e) => e.preventDefault()}
            onInteractOutside={(e) => {
              if (timeSelectingRef.current || timeOpeningRef.current) { e.preventDefault(); return }
              commitTime(); setIsTimeOpen(false)
            }}
          >
            <div
              ref={timePopoverRef}
              onPointerDown={(e) => { if ((e.target as HTMLElement).closest("button")) return; timeSelectingRef.current = true }}
              onPointerUp={() => { window.setTimeout(() => { timeSelectingRef.current = false }, 0) }}
            >
              <TimePickerDialog
                value={value}
                onChange={(time) => {
                  onChange?.(time)
                  setTimeEditText(null)
                  setIsTimeOpen(false)
                  window.setTimeout(() => timeInputRef.current?.focus(), 0)
                }}
                onClose={() => setIsTimeOpen(false)}
              />
            </div>
          </PopoverContent>
        </Popover>
      )
    }

    if (type === "datetime") {
      if (readOnly) {
        const committedDT = value ? tryParseDateTimeInput(value) ?? (isValidDate(new Date(value)) ? new Date(value) : null) : null
        const display = committedDT ? format(committedDT, DATETIME_DISPLAY_FORMAT) : ""
        return renderReadOnly(display)
      }
      const committedDT = value ? tryParseDateTimeInput(value) ?? (isValidDate(new Date(value)) ? new Date(value) : null) : null
      const dtDisplay = datetimeEditText !== null
        ? datetimeEditText
        : committedDT ? format(committedDT, DATETIME_DISPLAY_FORMAT) : ""

      const commitDT = () => {
        if (datetimeEditText === null) return
        if (!datetimeEditText.trim()) { onChange?.("") }
        else {
          const parsed = tryParseDateTimeInput(datetimeEditText, DATETIME_DISPLAY_FORMAT)
          if (parsed) onChange?.(parsed.toISOString())
        }
        setDatetimeEditText(null)
      }

      return (
        <Popover open={isDateTimeOpen} onOpenChange={() => {}}>
          <PopoverTrigger asChild>
            <div
              className={cn(
                "relative w-full flex items-center border-[2.5px] rounded-md transition-colors cursor-text",
                isError
                  ? "border-red-500"
                  : isDateTimeOpen
                    ? "border-[#8C57FF]"
                    : "border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500",
                disabled && "opacity-50 cursor-not-allowed",
              )}
              onMouseDown={() => {
                if (disabled) return
                if (!isDateTimeOpen) {
                  dtOpeningRef.current = true
                  setIsDateTimeOpen(true)
                  window.setTimeout(() => { dtOpeningRef.current = false }, 300)
                }
                window.setTimeout(() => dtInputRef.current?.focus(), 0)
              }}
            >
              <input
                ref={dtInputRef}
                type="text"
                value={dtDisplay}
                placeholder={placeholder || DATETIME_DISPLAY_FORMAT.toUpperCase()}
                disabled={disabled}
                autoComplete="off"
                onMouseDown={() => {
                  if (disabled) return
                  if (!isDateTimeOpen) {
                    dtOpeningRef.current = true
                    setIsDateTimeOpen(true)
                    window.setTimeout(() => { dtOpeningRef.current = false }, 300)
                  }
                }}
                onChange={(e) => {
                  setDatetimeEditText(e.target.value)
                  if (!isDateTimeOpen) setIsDateTimeOpen(true)
                  const parsed = tryParseDateTimeInput(e.target.value, DATETIME_DISPLAY_FORMAT)
                  if (parsed) onChange?.(parsed.toISOString())
                  else if (!e.target.value.trim()) onChange?.("")
                }}
                onFocus={() => { if (!isDateTimeOpen && !dtOpeningRef.current) setIsDateTimeOpen(true) }}
                onBlur={() => {
                  window.setTimeout(() => {
                    if (dtSelectingRef.current || dtOpeningRef.current) return
                    // If focus moved into the datetime popover (calendar or time inputs), keep open.
                    if (dtPopoverRef.current?.contains(document.activeElement)) return
                    commitDT(); setIsDateTimeOpen(false); onBlur?.()
                  }, 200)
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") { e.preventDefault(); commitDT(); setIsDateTimeOpen(false); dtInputRef.current?.blur() }
                  else if (e.key === "Escape") { setDatetimeEditText(null); setIsDateTimeOpen(false); dtInputRef.current?.blur() }
                }}
                className={cn(
                  "flex-1 bg-transparent text-base leading-[1.5em] py-3 pl-3 pr-1 focus:outline-none focus:ring-0",
                  isError ? "text-red-500 placeholder:text-red-300" : "text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500",
                  disabled && "cursor-not-allowed",
                )}
              />
              <span
                className="pr-3 cursor-pointer"
                onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setIsDateTimeOpen((p) => !p); window.setTimeout(() => dtInputRef.current?.focus(), 0) }}
              >
                <Clock className={cn("h-4 w-4", isError ? "text-red-500" : "text-gray-400 dark:text-gray-500")} />
              </span>
            </div>
          </PopoverTrigger>
          <PopoverContent
            onWheel={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
            className="w-auto p-0 bg-transparent border-none shadow-none"
            align="start"
            onOpenAutoFocus={(e) => e.preventDefault()}
            onInteractOutside={(e) => {
              if (dtSelectingRef.current || dtOpeningRef.current) { e.preventDefault(); return }
              commitDT(); setIsDateTimeOpen(false)
            }}
          >
            <div
              ref={dtPopoverRef}
              onPointerDown={(e) => { if ((e.target as HTMLElement).closest("button")) return; dtSelectingRef.current = true }}
              onPointerUp={() => { window.setTimeout(() => { dtSelectingRef.current = false }, 0) }}
            >
              <DateTimePickerDialog
                value={value}
                onChange={(datetime) => {
                  onChange?.(datetime)
                  setDatetimeEditText(null)
                  setIsDateTimeOpen(false)
                  window.setTimeout(() => dtInputRef.current?.focus(), 0)
                }}
                onClose={() => setIsDateTimeOpen(false)}
              />
            </div>
          </PopoverContent>
        </Popover>
      )
    }

    if (type === "multiselect") {
      if (readOnly) {
        const display = selectedValues
          .map((v) => options.find((o) => o.value === v)?.label ?? v)
          .join(", ")
        return renderReadOnly(display)
      }
      const filteredOptions = multiSearch
        ? options.filter((o) => o.label.toLowerCase().includes(multiSearch.toLowerCase()))
        : options

      const toggle = (val: string) => {
        const next = selectedValues.includes(val)
          ? selectedValues.filter((v) => v !== val)
          : [...selectedValues, val]
        onMultiChange?.(next)
      }

      const displayLabel = selectedValues.length === 0
        ? (placeholder || "Select options")
        : null

      return (
        <Popover open={isMultiOpen} onOpenChange={(open) => { setIsMultiOpen(open); if (!open) setMultiSearch("") }}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className={cn(
                "w-full min-h-[48px] px-3 py-2 border-[2.5px] rounded-md bg-transparent focus:outline-none focus:ring-0 transition-colors text-left flex flex-wrap items-center gap-1.5",
                isError
                  ? "border-red-500"
                  : "border-gray-300 dark:border-gray-600 focus:border-[#8C57FF] hover:border-gray-400 dark:hover:border-gray-500",
                selectedValues.length === 0 && "text-gray-500 dark:text-gray-400"
              )}
              onClick={onClick}
            >
              {displayLabel ? (
                <span className="text-base leading-[1.5em]">{displayLabel}</span>
              ) : (
                selectedValues.map((val) => {
                  const opt = options.find((o) => o.value === val)
                  return (
                    <span
                      key={val}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-[rgba(140,87,255,0.12)] text-[#8C57FF] border border-[rgba(140,87,255,0.3)]"
                    >
                      {opt?.label ?? val}
                      <span
                        role="button"
                        tabIndex={0}
                        onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); toggle(val) }}
                        className="ml-0.5 hover:text-[#6B3FCC] cursor-pointer"
                        aria-label={`Remove ${opt?.label ?? val}`}
                      >
                        <X className="h-3 w-3" />
                      </span>
                    </span>
                  )
                })
              )}
              {!hideChevron && (
                <ChevronDown className="ml-auto h-4 w-4 text-gray-500 dark:text-gray-400 shrink-0" />
              )}
            </button>
          </PopoverTrigger>
          <PopoverContent
            onWheel={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
            className="p-0 bg-white dark:bg-[#312D4B] shadow-lg border border-[rgba(46,38,61,0.12)] dark:border-[rgba(231,227,252,0.16)]"
            style={{ width: dropdownWidth || "var(--radix-popover-trigger-width)" }}
          >
            {/* Search */}
            <div className="p-2.5 border-b border-gray-200 dark:border-gray-600">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={multiSearch}
                  onChange={(e) => setMultiSearch(e.target.value)}
                  placeholder={`Search ${(label ?? "").toLowerCase()}...`}
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-md bg-transparent text-gray-900 dark:text-gray-100 focus:outline-none focus:border-[#8C57FF] placeholder:text-gray-400 dark:placeholder:text-gray-500"
                  autoFocus
                />
              </div>
            </div>

            {/* Options with checkboxes */}
            <div className="p-2.5 max-h-[280px] overflow-y-auto space-y-1 [&::-webkit-scrollbar]:w-[4px] [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-200 dark:[&::-webkit-scrollbar-thumb]:bg-gray-600 [&::-webkit-scrollbar-thumb]:rounded-full">
              {filteredOptions.length === 0 ? (
                <div className="px-3 py-4 text-center text-sm text-gray-500 dark:text-gray-400">No results found</div>
              ) : (
                filteredOptions.map((option) => {
                  const checked = selectedValues.includes(option.value)
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => toggle(option.value)}
                      className={cn(
                        "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors",
                        "bg-[#F4F5FA] dark:bg-[#28243D] hover:bg-[#E8E9EF] dark:hover:bg-[#332F4A]",
                        checked && "ring-2 ring-[#8C57FF]"
                      )}
                    >
                      <span
                        className={cn(
                          "flex-shrink-0 w-4 h-4 rounded border-2 flex items-center justify-center transition-colors",
                          checked
                            ? "bg-[#8C57FF] border-[#8C57FF]"
                            : "border-gray-400 dark:border-gray-500 bg-transparent"
                        )}
                      >
                        {checked && (
                          <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 12 12">
                            <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                      </span>
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{option.label}</span>
                    </button>
                  )
                })
              )}
            </div>

            {/* Add New + OK footer */}
            <div className="border-t border-gray-200 dark:border-gray-600">
              {onAddNew && (
                <button
                  type="button"
                  onClick={() => { setIsMultiOpen(false); onAddNew?.() }}
                  className="w-full px-4 py-3 text-left hover:bg-[#F4F5FA] dark:hover:bg-[#28243D] transition-colors text-sm text-[#8C57FF] font-medium flex items-center gap-2 border-b border-gray-200 dark:border-gray-600"
                >
                  <Plus className="h-4 w-4" />
                  {addNewLabel || `Add New ${label}`}
                </button>
              )}
              <div className="flex items-center justify-between px-3 py-2.5">
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {selectedValues.length} selected
                </span>
                <button
                  type="button"
                  onClick={() => { setIsMultiOpen(false); setMultiSearch("") }}
                  className="px-4 py-1.5 rounded-md bg-[#8C57FF] hover:bg-[#7C3AED] text-white text-sm font-medium transition-colors"
                >
                  OK
                </button>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      )
    }

    if (type === "link") {
      if (readOnly) {
        const selectedOption = options.find((opt) => opt.value === value)
        return renderReadOnly(selectedOption?.label ?? value)
      }
      const selectedOption = options.find((opt) => opt.value === value)
      const hasSelection = !!selectedOption
      const isEditing = editingText !== null

      // Display logic for the inline trigger input:
      //   - editing mode: show the user's typed editingText
      //   - dropdown open with no selection: show linkSearch
      //   - otherwise: show the selected label (or empty)
      const inputDisplay = isEditing
        ? (editingText as string)
        : isLinkOpen && !hasSelection
          ? linkSearch
          : selectedOption?.label || ""

      // Filter options by whichever search term is active.
      const searchTerm = isEditing ? (editingText as string) : linkSearch
      const filteredOptions = searchTerm
        ? options.filter(
            (opt) =>
              opt.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
              (opt.subtitle && opt.subtitle.toLowerCase().includes(searchTerm.toLowerCase()))
          )
        : options

      return (
        <Popover open={isLinkOpen} onOpenChange={() => {}}>
          <PopoverTrigger asChild>
            <div className={cn(
              "relative w-full flex items-center border-[2.5px] rounded-md transition-colors",
              isError
                ? "border-red-500"
                : isLinkOpen
                  ? "border-[#8C57FF]"
                  : "border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500"
            )}>
              <input
                ref={linkInputRef}
                type="text"
                value={inputDisplay}
                placeholder={placeholder || "Search or select..."}
                onChange={(e) => {
                  const newValue = e.target.value
                  if (hasSelection && !isEditing) {
                    // Begin editing the existing selection.
                    setEditingText(newValue)
                    setIsLinkOpen(true)
                  } else if (isEditing) {
                    setEditingText(newValue)
                  } else {
                    setLinkSearch(newValue)
                    onSearchChange?.(newValue)
                    if (!isLinkOpen) setIsLinkOpen(true)
                  }
                }}
                onMouseDown={() => {
                  // Empty field: open the popover on first click. The
                  // openingRef guards against the focus handler firing
                  // a second time and re-triggering this work.
                  if (!hasSelection) {
                    openingRef.current = true
                    setLinkSearch("")
                    setIsLinkOpen(true)
                    onClick?.()
                    setTimeout(() => {
                      openingRef.current = false
                    }, 300)
                  }
                }}
                onFocus={() => {
                  if (!hasSelection && !openingRef.current) {
                    setLinkSearch("")
                    setIsLinkOpen(true)
                    onClick?.()
                  }
                }}
                onBlur={() => {
                  // Defer so an option click (selectingRef) or another
                  // popover-internal interaction (openingRef) can win
                  // before we collapse the popover.
                  setTimeout(() => {
                    if (!selectingRef.current && !openingRef.current) {
                      // If the user was editing the existing selection
                      // and blurred with the input fully emptied, treat
                      // it as "clear this field" rather than reverting
                      // to the previous label. Partial deletes (some
                      // text still left) keep the old selection — they
                      // only need to pick something or fully clear.
                      if (editingText === "" && hasSelection) {
                        onChange?.("")
                      }
                      setIsLinkOpen(false)
                      setLinkSearch("")
                      setEditingText(null)
                    }
                  }, 200)
                }}
                className={cn(
                  "w-full px-3 py-3 text-base leading-[1.5em] bg-transparent focus:outline-none focus:ring-0 transition-colors rounded-md",
                  isError
                    ? "text-red-500 placeholder:text-red-300"
                    : "text-gray-900 dark:text-gray-100 placeholder:text-gray-500 dark:placeholder:text-gray-400"
                )}
              />
            </div>
          </PopoverTrigger>
          <PopoverContent
            onWheel={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
            className="p-0 bg-white dark:bg-[#312D4B] shadow-lg border border-[rgba(46,38,61,0.12)] dark:border-[rgba(231,227,252,0.16)]"
            style={{ width: dropdownWidth || "var(--radix-popover-trigger-width)" }}
            onOpenAutoFocus={(e) => e.preventDefault()}
            onInteractOutside={(e) => {
              if (openingRef.current || selectingRef.current) {
                e.preventDefault()
                return
              }
              // Mirror the empty-blur clear here. Radix fires this
              // handler synchronously on outside clicks BEFORE the
              // input's deferred onBlur runs — and it immediately
              // resets `editingText` to `null`, which makes the input
              // snap back to the previously selected label. We have
              // to wipe the selection in this exact moment so the
              // field truly starts over.
              if (editingText === "" && !!selectedOption) {
                onChange?.("")
              }
              setIsLinkOpen(false)
              setLinkSearch("")
              setEditingText(null)
            }}
          >
            {/* Options List — V5 (COA Parent dropdown): max-h bumped from
                300px to 60vh (cap at 420px). The COA tree can list dozens
                of group accounts; 300px capped visible rows at ~7 while
                the trigger input ate the remaining viewport, which the
                user read as "not scrolling". `overscroll-contain` keeps
                mousewheel inside the dropdown so scrolls don't leak into
                the page behind. */}
            <div className="p-2.5 max-h-[min(60vh,420px)] overflow-y-auto overscroll-contain space-y-2.5 [&::-webkit-scrollbar]:w-[4px] [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-200 dark:[&::-webkit-scrollbar-thumb]:bg-gray-600 [&::-webkit-scrollbar-thumb]:rounded-full">
              {filteredOptions.length === 0 ? (
                <div className="px-3 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                  No results found
                </div>
              ) : (
                filteredOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault()
                      selectingRef.current = true
                    }}
                    onClick={() => {
                      onChange?.(option.value)
                      setIsLinkOpen(false)
                      setLinkSearch("")
                      setEditingText(null)
                      // Clear selectingRef on the next tick so the
                      // outer onBlur (which fires synchronously after
                      // this click) still sees it as truthy.
                      setTimeout(() => {
                        selectingRef.current = false
                      }, 0)
                    }}
                    className={cn(
                      "w-full text-left transition-colors",
                      "bg-[#F4F5FA] dark:bg-[#28243D]",
                      "hover:bg-[#E8E9EF] dark:hover:bg-[#332F4A]",
                      "rounded-lg"
                    )}
                  >
                    <LinkOptionItem option={option} selected={option.value === value} />
                  </button>
                ))
              )}
            </div>

            {/* Add New Button */}
            {onAddNew && (
              <div className="border-t border-gray-200 dark:border-gray-600">
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    setIsLinkOpen(false)
                    setEditingText(null)
                    onAddNew?.()
                  }}
                  className="w-full px-4 py-3 text-left hover:bg-[#F4F5FA] dark:hover:bg-[#28243D] transition-colors text-sm text-[#8C57FF] font-medium flex items-center gap-2"
                >
                  <Plus className="h-4 w-4" />
                  {addNewLabel || `Add New ${label}`}
                </button>
              </div>
            )}
          </PopoverContent>
        </Popover>
      )
    }

    if (type === "color-select") {
      if (readOnly) {
        // Free-pick readOnly: show hex + swatch
        if (!options || options.length === 0) {
          return (
            <div className="relative w-full flex items-center">
              <div className={cn(
                "w-full px-3 py-3 border-[2.5px] rounded-md bg-transparent flex items-center gap-2",
                isError ? "border-red-500" : "border-gray-300 dark:border-gray-600"
              )}>
                <span className="text-base leading-[1.5em] text-gray-900 dark:text-gray-100 flex-1">
                  {value || <span className="text-gray-400">—</span>}
                </span>
                {value && (
                  <span
                    className="w-6 h-6 rounded-sm border border-black/10 shrink-0"
                    style={{ backgroundColor: value }}
                  />
                )}
              </div>
            </div>
          )
        }
        const selected = options.find((o) => o.value === value)
        return renderReadOnly(selected?.label ?? value)
      }

      // ── Free-pick mode (no options list) ──────────────────────────────────
      // Swatch trigger → "Select Color" modal (preset grid + custom hex input).
      if (!options || options.length === 0) {
        const PRESET_COLORS = [
          "#FF4C51","#FF9F43","#FFD700","#28C76F","#00CFE8","#8C57FF","#EA5455","#F08080",
          "#1E1E2D","#2D2D3F","#4A4A6A","#6E6B7B","#A8A4BC","#B0B0C0","#C8C8D8","#D8D8E8",
          "#FFFFFF","#F8F8FC","#F0EFF5","#E0DFF5","#D0CFE8","#C0BFD8","#B0AFC8","#A0A0B8",
        ]
        const [open, setOpen] = useState(false)
        const [draft, setDraft] = useState(value || "#8C57FF")
        const [hexInput, setHexInput] = useState(value || "#8C57FF")
        const customColorRef = useRef<HTMLInputElement>(null)
        const isValidHex = /^#[0-9A-Fa-f]{6}$/.test(value)
        const isDraftValidHex = /^#[0-9A-Fa-f]{6}$/.test(draft)

        const handleOpen = () => {
          setDraft(value || "#8C57FF")
          setHexInput(value || "#8C57FF")
          setOpen(true)
        }

        const handleHexInput = (v: string) => {
          setHexInput(v)
          const normalised = v.startsWith("#") ? v : `#${v}`
          if (/^#[0-9A-Fa-f]{6}$/.test(normalised)) setDraft(normalised)
        }

        const handleApply = () => {
          if (isDraftValidHex) { onChange?.(draft); setOpen(false) }
        }

        return (
          <>
            {/* Field trigger */}
            <button
              type="button"
              onClick={handleOpen}
              className={cn(
                "relative w-full flex items-center border-[2.5px] rounded-md bg-transparent transition-colors overflow-hidden",
                isError
                  ? "border-red-500"
                  : "border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 focus:border-[#8C57FF]"
              )}
            >
              {/* Hex label */}
              <span className={cn(
                "flex-1 pl-3 py-3 text-base leading-[1.5em] text-left font-mono",
                value ? "text-gray-900 dark:text-gray-100" : "text-gray-400 dark:text-gray-500"
              )}>
                {value || (placeholder || "#000000")}
              </span>
              {/* Colour swatch — padded square inset at the right */}
              <span className="pr-3 pl-2 shrink-0 flex items-center">
                <span
                  className="w-7 h-7 rounded-md border border-black/10 dark:border-white/10 shrink-0 block"
                  style={{ backgroundColor: isValidHex ? value : "#8C57FF" }}
                />
              </span>
            </button>

            {/* Select Color modal */}
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogContent className="sm:max-w-[360px] bg-white dark:bg-[#312D4B] border border-[rgba(46,38,61,0.12)] dark:border-[rgba(231,227,252,0.16)] p-6 gap-0">
                <DialogHeader className="mb-4">
                  <DialogTitle className="text-base font-semibold text-gray-900 dark:text-gray-100">
                    Select Color
                  </DialogTitle>
                </DialogHeader>

                {/* Preview + hex input */}
                <div className="flex items-center gap-3 mb-5">
                  <span
                    className="w-10 h-10 rounded-md border border-black/10 shrink-0"
                    style={{ backgroundColor: isDraftValidHex ? draft : "#8C57FF" }}
                  />
                  <input
                    type="text"
                    value={hexInput}
                    onChange={(e) => handleHexInput(e.target.value)}
                    placeholder="#8C57FF"
                    className="flex-1 px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-[#28243D] text-sm font-mono text-gray-900 dark:text-gray-100 focus:outline-none focus:border-[#8C57FF]"
                  />
                </div>

                {/* Preset Colors */}
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2">Preset Colors</p>
                <div className="grid grid-cols-8 gap-1.5 mb-5">
                  {PRESET_COLORS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => { setDraft(c); setHexInput(c) }}
                      className={cn(
                        "w-7 h-7 rounded-md border-2 transition-transform hover:scale-110",
                        draft === c
                          ? "border-[#8C57FF] scale-110"
                          : "border-transparent hover:border-gray-300 dark:hover:border-gray-500"
                      )}
                      style={{ backgroundColor: c }}
                      title={c}
                    />
                  ))}
                </div>

                {/* Custom Color */}
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2">Custom Color</p>
                <div className="flex items-center gap-3 mb-6">
                  <button
                    type="button"
                    onClick={() => customColorRef.current?.click()}
                    className="w-7 h-7 rounded-md border-2 border-transparent hover:border-gray-300 dark:hover:border-gray-500 transition-transform hover:scale-110 shrink-0"
                    style={{ backgroundColor: isDraftValidHex ? draft : "#8C57FF" }}
                    title="Open color wheel"
                  />
                  <span className="text-sm text-gray-500 dark:text-gray-400">Click to open the color wheel for a custom color</span>
                  <input
                    ref={customColorRef}
                    type="color"
                    value={isDraftValidHex ? draft : "#8C57FF"}
                    onChange={(e) => { setDraft(e.target.value); setHexInput(e.target.value) }}
                    className="sr-only"
                    tabIndex={-1}
                    aria-hidden="true"
                  />
                </div>

                <DialogFooter className="gap-2">
                  <Button variant="outline" onClick={() => setOpen(false)} className="flex-1">
                    Cancel
                  </Button>
                  <Button
                    onClick={handleApply}
                    disabled={!isDraftValidHex}
                    className="flex-1 bg-primary hover:bg-primary/90 text-white"
                  >
                    Apply
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        )
      }

      // ── Preset-options mode (options list provided) ───────────────────────
      const [isColorOpen, setIsColorOpen] = useState(false)
      const selected = options.find((o) => o.value === value)
      return (
        <Popover open={isColorOpen} onOpenChange={setIsColorOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className={cn(
                "w-full px-3 py-3 border-[2.5px] rounded-md bg-transparent focus:outline-none focus:ring-0 transition-colors text-left flex items-center justify-between",
                isError
                  ? "border-red-500 text-red-500"
                  : "border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 focus:border-[#8C57FF] hover:border-gray-400 dark:hover:border-gray-500",
                !selected && !isError && "text-gray-500 dark:text-gray-400"
              )}
              onClick={onClick}
            >
              <span className="flex items-center gap-2">
                {selected?.data?.color && (
                  <span
                    className="w-3 h-3 rounded-full shrink-0 inline-block"
                    style={{ backgroundColor: selected.data.color }}
                  />
                )}
                <span className="text-base leading-[1.5em]">{selected?.label || placeholder || "Select"}</span>
              </span>
              <ChevronDown className="h-4 w-4 text-gray-500 dark:text-gray-400 shrink-0" />
            </button>
          </PopoverTrigger>
          <PopoverContent
            onWheel={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
            className="p-1.5 bg-white dark:bg-[#312D4B] shadow-lg border border-[rgba(46,38,61,0.12)] dark:border-[rgba(231,227,252,0.16)]"
            style={{ width: dropdownWidth || "var(--radix-popover-trigger-width)" }}
          >
            <div className="max-h-[240px] overflow-y-auto space-y-0.5 [&::-webkit-scrollbar]:w-[4px] [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-200 dark:[&::-webkit-scrollbar-thumb]:bg-gray-600 [&::-webkit-scrollbar-thumb]:rounded-full">
              {options.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => { onChange?.(option.value); setIsColorOpen(false) }}
                  className={cn(
                    "w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-left text-sm transition-colors",
                    "hover:bg-[#F4F5FA] dark:hover:bg-[#28243D]",
                    option.value === value && "bg-[rgba(140,87,255,0.08)] font-semibold text-[#8C57FF]"
                  )}
                >
                  {option.data?.color && (
                    <span
                      className="w-3 h-3 rounded-full shrink-0 inline-block"
                      style={{ backgroundColor: option.data.color }}
                    />
                  )}
                  <span className={cn("text-gray-900 dark:text-gray-100", option.value === value && "text-[#8C57FF]")}>
                    {option.label}
                  </span>
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      )
    }

    if (type === "radio") {
      if (readOnly) {
        const selected = options.find((o) => o.value === value)
        return renderReadOnly(selected?.label ?? value)
      }
      return (
        <div
          className={cn(
            "w-full px-3 py-3 text-base leading-[1.5em] border-[2.5px] rounded-md bg-transparent flex flex-wrap",
            radioDirection === "vertical" ? "flex-col items-start gap-3" : "items-center gap-6",
            isError
              ? "border-red-500"
              : "border-gray-300 dark:border-gray-600 focus-within:border-[#8C57FF] hover:border-gray-400 dark:hover:border-gray-500"
          )}
          onClick={onClick}
        >
          {options.map((option) => (
            <label
              key={option.value}
              className="flex items-center gap-2 text-gray-900 dark:text-gray-100 cursor-pointer select-none"
            >
              <input
                type="radio"
                name={label}
                value={option.value}
                checked={value === option.value}
                onChange={() => onChange?.(option.value)}
                required={required}
                disabled={disabled}
                className="h-4 w-4 accent-[#8C57FF] cursor-pointer"
              />
              {option.label}
            </label>
          ))}
        </div>
      )
    }

    if (type === "select") {
      if (readOnly) {
        const selected = options.find((o) => o.value === value)
        return renderReadOnly(selected?.label ?? value)
      }
      return (
        <div className="relative w-full">
          <select
            value={value}
            onChange={(e) => onChange?.(e.target.value)}
            className={cn(
              "w-full px-3 py-3 text-base leading-[1.5em] border-[2.5px] rounded-md bg-transparent focus:outline-none focus:ring-0 transition-colors appearance-none",
              isError
                ? "border-red-500 text-red-500"
                : "border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 focus:border-[#8C57FF] hover:border-gray-400 dark:hover:border-gray-500"
            )}
            required={required}
            onClick={onClick}
          >
            <option value="" className="bg-white dark:bg-[#312D4B] text-gray-900 dark:text-gray-100">
              {placeholder || "Select"}
            </option>
            {options.map((option) => (
              <option
                key={option.value}
                value={option.value}
                className="bg-white dark:bg-[#312D4B] text-gray-900 dark:text-gray-100"
              >
                {option.label}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 dark:text-gray-400 pointer-events-none" />
        </div>
      )
    }

    if (type === "textarea") {
      if (readOnly) {
        return (
          <textarea
            value={value}
            readOnly
            disabled
            rows={rows}
            placeholder={placeholder}
            className={cn(
              "w-full px-3 py-3 text-base leading-[1.5em] border-[2.5px] rounded-md resize-none cursor-not-allowed",
              "bg-gray-100 dark:bg-[#28243D]",
              isError
                ? "border-red-300 text-red-400"
                : "border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400",
              valueClassName
            )}
          />
        )
      }
      return (
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          onBlur={onBlur}
          rows={rows}
          placeholder={placeholder}
          className={cn(
            "w-full px-3 py-3 text-base leading-[1.5em] border-[2.5px] rounded-md bg-transparent focus:outline-none focus:ring-0 transition-colors resize-none",
            isError
              ? "border-red-500 text-red-500 placeholder:text-red-300"
              : "border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 focus:border-[#8C57FF] hover:border-gray-400 dark:hover:border-gray-500"
          )}
          required={required}
          onClick={onClick}
        />
      )
    }

    if (type === "tag") {
      if (readOnly) return renderReadOnly(tags.join(", "))
      return (
        <div
          className={cn(
            "flex flex-wrap items-center gap-1.5 w-full min-h-[48px] px-3 py-2 border-[2.5px] rounded-md bg-transparent transition-colors cursor-text",
            isError
              ? "border-red-500"
              : tagFocused
                ? "border-[#8C57FF]"
                : "border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500"
          )}
          onClick={() => tagInputRef.current?.focus()}
        >
          {tags.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-[rgba(140,87,255,0.12)] text-[#8C57FF] border border-[rgba(140,87,255,0.3)] shrink-0"
            >
              {tag}
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => onTagsChange?.(tags.filter((t) => t !== tag))}
                className="hover:text-[#6B3FCC] leading-none"
                aria-label={`Remove ${tag}`}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
          <input
            ref={tagInputRef}
            type="text"
            value={tagInput}
            onChange={handleTagChange}
            onKeyDown={handleTagKeyDown}
            onFocus={() => setTagFocused(true)}
            onBlur={() => setTagFocused(false)}
            placeholder={tags.length === 0 ? (placeholder || "Type and press Enter or comma to add") : ""}
            className="flex-1 min-w-[140px] bg-transparent text-base leading-[1.5em] text-gray-900 dark:text-gray-100 placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:outline-none"
          />
        </div>
      )
    }

    // ── Barcode ──────────────────────────────────────────────────────────────
    if (type === "barcode") {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const [showCameraDialog, setShowCameraDialog] = useState(false)

      const handleCameraCapture = () => {
        setShowCameraDialog(false)
      }

      if (readOnly) return renderReadOnly(value)
      return (
        <>
          <div className={cn(
            "relative w-full flex items-center border-[2.5px] rounded-md transition-colors overflow-hidden",
            isError
              ? "border-red-500"
              : "border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 focus-within:border-[#8C57FF]"
          )}>
            <input
              type="text"
              value={value}
              onChange={(e) => onChange?.(e.target.value)}
              onBlur={onBlur}
              placeholder={placeholder || "Scan or enter barcode"}
              className={cn(
                "flex-1 px-3 py-3 text-base leading-[1.5em] bg-transparent focus:outline-none focus:ring-0 transition-colors",
                isError ? "text-red-500 placeholder:text-red-300" : "text-gray-900 dark:text-gray-100",
              )}
              required={required}
            />
            <button
              type="button"
              onClick={() => setShowCameraDialog(true)}
              title="Scan barcode with camera"
              className="px-3 text-gray-500 hover:text-primary dark:text-gray-400 dark:hover:text-primary transition-colors shrink-0"
            >
              <Camera className="h-4 w-4" />
            </button>
          </div>

          {/* Camera Scanner Dialog */}
          <Dialog open={showCameraDialog} onOpenChange={setShowCameraDialog}>
            <DialogContent className="sm:max-w-[480px] bg-white dark:bg-[#312D4B] border-gray-200 dark:border-[rgba(231,227,252,0.15)]">
              <DialogHeader>
                <DialogTitle>Scan Barcode</DialogTitle>
              </DialogHeader>
              <div className="flex flex-col items-center gap-4 py-4">
                <div className="w-full h-52 bg-gray-900 rounded-lg flex items-center justify-center relative overflow-hidden">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="border-2 border-primary w-56 h-28 rounded-lg" />
                  </div>
                  <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-primary/70 animate-pulse" />
                  <p className="text-white text-sm z-10 absolute bottom-4">Position barcode within the frame</p>
                </div>
                <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary text-center">
                  Point your camera at the barcode to scan it automatically.
                </p>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowCameraDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={handleCameraCapture} className="bg-primary hover:bg-primary/90 text-white">
                  <Camera className="h-4 w-4 mr-2" />
                  Capture
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      )
    }

    // text (default) — readOnly uses the shared display
    if (readOnly) {
      const display = suffix ? `${value}${suffix}` : value
      return renderReadOnly(display)
    }

    return (
      <div className={cn("relative w-full flex items-center border-[2.5px] rounded-md transition-colors overflow-hidden",
        isError
          ? "border-red-500"
          : "border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 focus-within:border-[#8C57FF]"
      )}>
        {icon && (
          <div className="pl-3 text-gray-400 pointer-events-none shrink-0">
            {icon}
          </div>
        )}
        <input
          type={type === "email" ? "email" : type === "tel" ? "tel" : "text"}
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          onBlur={onBlur}
          placeholder={placeholder}
          className={cn(
            "flex-1 py-3 text-base leading-[1.5em] bg-transparent focus:outline-none focus:ring-0 transition-colors",
            icon ? "pl-2 pr-3" : "px-3",
            isError
              ? "text-red-500 placeholder:text-red-300"
              : "text-gray-900 dark:text-gray-100",
          )}
          required={required}
          onClick={onClick}
        />
        {suffix && (
          <span className="px-3 py-3 text-sm bg-muted text-muted-foreground select-none whitespace-nowrap border-l border-gray-300 dark:border-gray-600 shrink-0">
            {suffix}
          </span>
        )}
      </div>
    )
  }

  // When rendered inside a table cell (`cellMode`), suppress the rounded
  // outline border on every interactive descendant — the cell's column
  // divider is the only border that should remain visible. The selectors
  // only reach DOM nodes inside this wrapper, so popover content (which
  // renders in a portal) keeps its own internal borders/rounding.
  // Mirror the table-cell flatten rules but exempt the shadcn
  // Checkbox primitive — it's `<button role="checkbox">` whose
  // visible square frame comes from `border-2`. Without
  // `:not([role=checkbox])` the unscoped `[&_button]:!border-0` rule
  // erases that frame and the checkbox effectively disappears (this
  // was the "Share Screen / Mute on Timeout columns are not showing"
  // symptom inside the Agenda table).
  const cellModeOverride = cellMode
    ? "[&_input:not([type=checkbox])]:!border-0 [&_input:not([type=checkbox])]:!rounded-none " +
      "[&_input:not([type=checkbox])]:!leading-normal [&_input:not([type=checkbox])]:!py-2 " +
      "[&_textarea]:!border-0 [&_textarea]:!rounded-none " +
      "[&_select]:!border-0 [&_select]:!rounded-none " +
      "[&_button:not([role=checkbox])]:!border-0 [&_button:not([role=checkbox])]:!rounded-none " +
      "[&>div]:!border-0 [&>div]:!rounded-none"
    : ""

  return (
    <div
      className={cn(
        "relative",
        className,
        disabled && "opacity-50 pointer-events-none select-none",
        cellModeOverride,
      )}
    >
      {renderInput()}
      {!cellMode && !autoPopulated && (
        <label
          className={cn(
            "absolute -top-2 left-3 px-1 text-sm font-medium pointer-events-none",
            !labelStyle && (labelBg ?? "bg-white dark:bg-[#312D4B]"),
            isError ? "text-red-500" : "text-gray-700 dark:text-gray-300",
            readOnly && "opacity-60"
          )}
          style={labelStyle}
        >
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      {isError && displayHelperText && (
        <p className="text-xs text-red-500 mt-1">{displayHelperText}</p>
      )}
    </div>
  )
}
